import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import NoteModal from '../components/notes/NoteModal'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import EditTimeCountdown from '../components/market/EditTimeCountdown'
import { NotesService } from '../services/notes.service'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { TransactionsService } from '../services/intellijetnet/transactions.service'
import { EventsService } from '../services/intellijetnet/events.service'
import { AircraftNote } from '../types/database'
import { Aircraft } from '../types/intellijetnet'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../hooks/useProfile'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'

type NoteWithAuthor = AircraftNote & { author_name: string }

export default function NotesPage() {
  const { modelId, aircraftId, transId, eventId } = useParams<{
    modelId: string;
    aircraftId: string;
    transId: string;
    eventId: string;
  }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const { profile } = useProfile()
  const location = useLocation()

  // Determine the correct back URL based on current path
  const getBackUrl = () => {
    const currentPath = location.pathname
    if (currentPath.includes('/for-sale/')) {
      return `/market/${modelId}/for-sale/${aircraftId}`
    } else if (currentPath.includes('/off-market/')) {
      return `/market/${modelId}/off-market/${aircraftId}`
    } else if (currentPath.includes('/sold/')) {
      return `/market/${modelId}/sold/${transId}`
    } else if (currentPath.includes('/removed/')) {
      return `/market/${modelId}/removed/${eventId}`
    } else if (currentPath.includes('/aircraft/')) {
      // For unified route, check if there's a referrer in the location state
      const state = location.state as { from?: string } | null
      if (state?.from) {
        return state.from
      }
      // Default fallback to for-sale page
      return `/market/${modelId}/for-sale/${aircraftId}`
    }
    // Fallback
    return `/market/${modelId}/for-sale/${aircraftId}`
  }

  const [notes, setNotes] = useState<NoteWithAuthor[]>([])
  const [aircraft, setAircraft] = useState<Aircraft | null>(null)
  const [resolvedAircraftId, setResolvedAircraftId] = useState<number | null>(null)
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [editingNote, setEditingNote] = useState<NoteWithAuthor | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const editNoteId = searchParams.get('edit')

  useEffect(() => {
    if (modelId && (aircraftId || transId || eventId)) {
      fetchData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, aircraftId, transId, eventId])

  useEffect(() => {
    // Handle edit query parameter
    if (editNoteId && notes.length > 0) {
      const noteToEdit = notes.find(note => note.id === editNoteId)
      if (noteToEdit && canEditNote(noteToEdit)) {
        setEditingNote(noteToEdit)
        setShowNoteModal(true)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNoteId, notes])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const numericModelId = parseInt(modelId!, 10)
      if (isNaN(numericModelId)) {
        throw new Error('Invalid model ID')
      }

      let actualAircraftId: number
      const currentPath = location.pathname

      // Determine the aircraft ID based on the route type
      if (aircraftId) {
        // Direct aircraft routes (for-sale, off-market, unified)
        actualAircraftId = parseInt(aircraftId, 10)
        if (isNaN(actualAircraftId)) {
          throw new Error('Invalid aircraft ID')
        }
      } else if (transId) {
        // Sold route - get aircraft ID from transaction
        const numericTransId = parseInt(transId, 10)
        if (isNaN(numericTransId)) {
          throw new Error('Invalid transaction ID')
        }
        const transaction = await TransactionsService.getById(numericTransId)
        if (!transaction || !transaction.aircraft_id) {
          throw new Error('Transaction not found or has no associated aircraft')
        }
        actualAircraftId = transaction.aircraft_id

        // Redirect to unified route if we're on legacy route
        if (currentPath.includes('/sold/')) {
          navigate(`/market/${modelId}/aircraft/${actualAircraftId}/notes`, {
            replace: true,
            state: { from: `/market/${modelId}/sold/${transId}` }
          })
          return
        }
      } else if (eventId) {
        // Removed route - get aircraft ID from event
        const event = await EventsService.getByIdWithFallback(eventId)
        if (!event) {
          // For numeric event IDs that can't be found, try fallback
          if (/^\d+$/.test(eventId)) {
            // Event ID not found, attempting to treat as aircraft_id for fallback
            try {
              const fallbackAircraft = await AircraftService.getById(parseInt(eventId, 10))
              if (fallbackAircraft) {
                navigate(`/market/${modelId}/aircraft/${fallbackAircraft.aircraft_id}/notes`, {
                  replace: true,
                  state: { from: `/market/${modelId}/for-sale/${fallbackAircraft.aircraft_id}` }
                })
                return
              }
            } catch (fallbackError) {
              // Fallback aircraft lookup failed
            }

            throw new Error(`Unable to access notes for this aircraft. The event record (ID: ${eventId}) appears to be invalid or missing from the database. This is a known data inconsistency issue. Please contact your administrator to resolve this, or try accessing the aircraft through a different section if available.`)
          }
          throw new Error(`Event ${eventId} not found`)
        }

        if (!event.aircraft_id) {
          throw new Error(`Event ${eventId} has no associated aircraft`)
        }

        actualAircraftId = event.aircraft_id

        // Redirect to unified route if we're on legacy route
        if (currentPath.includes('/removed/')) {
          navigate(`/market/${modelId}/aircraft/${actualAircraftId}/notes`, {
            replace: true,
            state: { from: `/market/${modelId}/removed/${eventId}` }
          })
          return
        }
      } else {
        throw new Error('No valid ID provided')
      }

      // If we're on a legacy route (for-sale or off-market), redirect to unified route
      if (currentPath.includes('/for-sale/') || currentPath.includes('/off-market/')) {
        const fromPath = currentPath.includes('/for-sale/')
          ? `/market/${modelId}/for-sale/${aircraftId}`
          : `/market/${modelId}/off-market/${aircraftId}`

        navigate(`/market/${modelId}/aircraft/${actualAircraftId}/notes`, {
          replace: true,
          state: { from: fromPath }
        })
        return
      }

      // Store the resolved aircraft ID
      setResolvedAircraftId(actualAircraftId)

      // Fetch model info and aircraft data in parallel
      const [modelInfoData, aircraftData, notesData] = await Promise.all([
        ModelInfoCacheService.getModelInfo(numericModelId),
        AircraftService.getById(actualAircraftId),
        NotesService.getByAircraftIdWithAuthors(actualAircraftId)
      ])

      if (!modelInfoData) {
        throw new Error('Model not found')
      }
      if (!aircraftData) {
        throw new Error('Aircraft not found')
      }

      setModelInfo(modelInfoData)
      setAircraft(aircraftData)
      setNotes(notesData)
    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const isAdmin = profile?.account_type === 'admin' || profile?.account_type === 'global_admin'

  const canEditNote = (note: NoteWithAuthor): boolean => {
    if (!user) return false
    return NotesService.canEditNote(note, user.id, isAdmin)
  }

  const handleCreateNote = () => {
    setEditingNote(null)
    setShowNoteModal(true)
  }

  const handleEditNote = (note: NoteWithAuthor) => {
    if (canEditNote(note)) {
      setEditingNote(note)
      setShowNoteModal(true)
    }
  }

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return
    }

    try {
      setDeleting(noteId)
      await NotesService.delete(noteId)
      setNotes(prev => prev.filter(note => note.id !== noteId))
    } catch (err: any) {
      alert('Failed to delete note: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  const handleNoteSaved = (_savedNote: AircraftNote) => {
    // Refresh notes to get updated data with author info
    fetchData()
    setShowNoteModal(false)
    setEditingNote(null)
    // Clear edit query parameter
    if (editNoteId) {
      setSearchParams({})
    }
  }

  const handleModalClose = () => {
    setShowNoteModal(false)
    setEditingNote(null)
    // Clear edit query parameter
    if (editNoteId) {
      setSearchParams({})
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
      </Layout>
    )
  }

  if (error || !aircraft || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Data not found'}</div>
            <button
              onClick={() => navigate(getBackUrl())}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Aircraft Details
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                onClick={() => navigate(getBackUrl())}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Aircraft Details
              </button>
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-purple-950">
                  Notes - {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)} - {aircraft.sernbr}
                </h1>
                <button
                  onClick={handleCreateNote}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  New Note
                </button>
              </div>
            </div>

            <div className="bg-white shadow-sm rounded-lg">
              {notes.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-lg text-gray-500 mb-4">No notes yet for this aircraft</div>
                  <button
                    onClick={handleCreateNote}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Add First Note
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {notes.map((note) => (
                    <div key={note.id} className="p-6 hover:bg-gray-50 transition-colors duration-150">
                      <div className="flex justify-between items-start mb-3">
                        <div className="text-sm text-gray-500">
                          {formatDate(note.created_at)} <span className="font-semibold text-indigo-600">{note.author_name}</span>
                        </div>
                        {canEditNote(note) && (
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEditNote(note)}
                              className="text-sm text-gray-400 hover:text-gray-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={deleting === note.id}
                              className="text-sm text-red-400 hover:text-red-600 disabled:opacity-50"
                            >
                              {deleting === note.id ? 'Deleting...' : 'Delete'}
                            </button>
                            {!isAdmin && (
                              <EditTimeCountdown
                                createdAt={note.created_at}
                                className="ml-2"
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                        {note.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Note Modal */}
      {resolvedAircraftId && (
        <NoteModal
          isOpen={showNoteModal}
          onClose={handleModalClose}
          aircraftId={resolvedAircraftId}
          note={editingNote}
          onSave={handleNoteSaved}
        />
      )}
    </Layout>
  )
}
